import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatMoneyCompact } from "../../../helpers/dashboard/commandDashboardModel";

/** Measured container width so SVG text is never stretched (phone and desktop share the same charts). */
function useMeasuredWidth(fallback) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const update = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

export const CHART_COLORS = Object.freeze({
  in: "#4ee2a1",
  out: "#5b9bf0",
  net: "#e3b84d",
  red: "#ef6a5e",
  gold: "#e3b84d",
  blue: "#5b9bf0",
  green: "#4ee2a1",
  grey: "#7d8696",
  purple: "#9b7bf0",
});

function axisMoney(value) {
  const abs = Math.abs(value);
  if (abs >= 1000) return `$${Math.round(value / 100) / 10}K`.replace(".0K", "K");
  return `$${Math.round(value)}`;
}

/** Money in / money out areas with a net line, legend totals live in the widget header. */
export function CashFlowChart({ buckets, ariaLabel }) {
  const [hover, setHover] = useState(null);
  const [wrapRef, W] = useMeasuredWidth(640);
  const H = W < 520 ? 210 : 230;
  const padL = 46;
  const padR = 12;
  const padT = 12;
  const padB = 26;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const { max, min } = useMemo(() => {
    const values = buckets.flatMap((b) => [b.in, b.out, b.net]);
    return { max: Math.max(1, ...values), min: Math.min(0, ...values) };
  }, [buckets]);

  const x = (i) => padL + (buckets.length <= 1 ? innerW / 2 : (i / (buckets.length - 1)) * innerW);
  const y = (v) => padT + innerH - ((v - min) / (max - min || 1)) * innerH;
  const line = (key) => buckets.map((b, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(b[key]).toFixed(1)}`).join(" ");
  const area = (key) =>
    `${line(key)} L${x(buckets.length - 1).toFixed(1)},${y(0).toFixed(1)} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z`;

  const ticks = [0, 1, 2, 3].map((n) => min + ((max - min) * n) / 3);
  const labelEvery = Math.max(1, Math.ceil(buckets.length / 6));

  const onMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((px - padL) / innerW) * (buckets.length - 1));
    setHover(Math.max(0, Math.min(buckets.length - 1, idx)));
  };

  const active = hover != null ? buckets[hover] : null;

  return (
    <div className="cmd-chart cmd-chart--area" ref={wrapRef}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={ariaLabel}
        onPointerMove={onMove}
        onPointerDown={onMove}
        onPointerLeave={() => setHover(null)}
        style={{ touchAction: "pan-y", height: H }}
      >
        <defs>
          <linearGradient id="cmd-in" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CHART_COLORS.in} stopOpacity="0.38" />
            <stop offset="100%" stopColor={CHART_COLORS.in} stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="cmd-out" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CHART_COLORS.out} stopOpacity="0.38" />
            <stop offset="100%" stopColor={CHART_COLORS.out} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} className="cmd-chart__grid" />
            <text x={padL - 8} y={y(t) + 4} textAnchor="end" className="cmd-chart__axis">
              {axisMoney(t)}
            </text>
          </g>
        ))}
        {buckets.map((b, i) =>
          i % labelEvery === 0 ? (
            <text key={b.fromIso} x={x(i)} y={H - 6} textAnchor="middle" className="cmd-chart__axis">
              {b.label.split(" – ")[0]}
            </text>
          ) : null,
        )}
        <path d={area("in")} fill="url(#cmd-in)" />
        <path d={area("out")} fill="url(#cmd-out)" />
        <path d={line("in")} fill="none" stroke={CHART_COLORS.in} strokeWidth="2" />
        <path d={line("out")} fill="none" stroke={CHART_COLORS.out} strokeWidth="2" />
        <path d={line("net")} fill="none" stroke={CHART_COLORS.net} strokeWidth="2" strokeDasharray="5 4" />
        {active ? (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={padT} y2={padT + innerH} className="cmd-chart__cursor" />
            {["in", "out", "net"].map((k) => (
              <circle key={k} cx={x(hover)} cy={y(active[k])} r="4" fill={CHART_COLORS[k]} />
            ))}
          </g>
        ) : null}
      </svg>
      {active ? (
        <div
          className="cmd-chart__tip"
          style={{ left: `${(x(hover) / W) * 100}%` }}
          role="status"
        >
          <strong>{active.label}</strong>
          <span><i style={{ background: CHART_COLORS.in }} />Money In {formatMoneyCompact(active.in)}</span>
          <span><i style={{ background: CHART_COLORS.out }} />Money Out {formatMoneyCompact(active.out)}</span>
          <span><i style={{ background: CHART_COLORS.net }} />Net {formatMoneyCompact(active.net)}</span>
        </div>
      ) : null}
    </div>
  );
}

/** Donut with a centre total; segments with 0 are skipped but stay in the legend. */
export function Donut({ segments, total, centerLabel, ariaLabel }) {
  const R = 52;
  const C = 2 * Math.PI * R;
  let offset = 0;
  const sum = segments.reduce((s, seg) => s + seg.value, 0);
  return (
    <svg viewBox="0 0 140 140" className="cmd-donut" role="img" aria-label={ariaLabel}>
      <circle cx="70" cy="70" r={R} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="16" />
      {sum > 0
        ? segments.map((seg) => {
            if (seg.value <= 0) return null;
            const len = (seg.value / sum) * C;
            const el = (
              <circle
                key={seg.key}
                cx="70"
                cy="70"
                r={R}
                fill="none"
                stroke={seg.color}
                strokeWidth="16"
                strokeDasharray={`${Math.max(0, len - 2)} ${C - Math.max(0, len - 2)}`}
                strokeDashoffset={-offset}
                transform="rotate(-90 70 70)"
              />
            );
            offset += len;
            return el;
          })
        : null}
      <text x="70" y="68" textAnchor="middle" className="cmd-donut__num">{total}</text>
      <text x="70" y="86" textAnchor="middle" className="cmd-donut__lbl">{centerLabel}</text>
    </svg>
  );
}

/** Vertical bars per day; the y axis is scaled to the data (no invented target line). */
export function DayBars({ days, ariaLabel, color = CHART_COLORS.blue }) {
  const max = Math.max(1, ...days.map((d) => d.hours));
  const top = Math.ceil(max / 5) * 5 || 5;
  const [wrapRef, W] = useMeasuredWidth(420);
  const H = 150;
  const padL = 34;
  const padB = 24;
  const padT = 8;
  const innerH = H - padB - padT;
  const slot = (W - padL - 6) / days.length;
  const barW = Math.min(30, slot * 0.5);
  return (
    <div ref={wrapRef} className="cmd-bars-wrap">
    <svg viewBox={`0 0 ${W} ${H}`} className="cmd-chart cmd-bars" role="img" aria-label={ariaLabel} style={{ height: H }}>
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line x1={padL} x2={W - 4} y1={padT + innerH * (1 - f)} y2={padT + innerH * (1 - f)} className="cmd-chart__grid" />
          <text x={padL - 6} y={padT + innerH * (1 - f) + 4} textAnchor="end" className="cmd-chart__axis">
            {Math.round(top * f)}
          </text>
        </g>
      ))}
      {days.map((d, i) => {
        const h = (d.hours / top) * innerH;
        const cx = padL + slot * i + slot / 2;
        return (
          <g key={d.iso}>
            <rect x={cx - barW / 2} y={padT + innerH - h} width={barW} height={Math.max(0, h)} rx="3" fill={color}>
              <title>{`${d.weekday}: ${d.hours} hrs`}</title>
            </rect>
            <text x={cx} y={H - 6} textAnchor="middle" className="cmd-chart__axis">{d.weekday}</text>
          </g>
        );
      })}
    </svg>
    </div>
  );
}

const FUNNEL_COLORS = [CHART_COLORS.blue, CHART_COLORS.purple, CHART_COLORS.gold, CHART_COLORS.green];

/** Stacked trapezoid funnel; widths follow stage counts (floor keeps empty stages visible). */
export function Funnel({ stages, ariaLabel }) {
  const H = 150;
  const W = 150;
  const row = H / stages.length;
  const max = Math.max(1, ...stages.map((s) => s.count));
  const half = (count) => (W / 2) * (0.28 + 0.72 * (count / max));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="cmd-funnel" role="img" aria-label={ariaLabel}>
      {stages.map((stage, i) => {
        const top = half(stage.count);
        const next = stages[i + 1] ? half(stages[i + 1].count) : top * 0.7;
        const y0 = i * row + 1;
        const y1 = (i + 1) * row - 1;
        const cx = W / 2;
        return (
          <polygon
            key={stage.key}
            points={`${cx - top},${y0} ${cx + top},${y0} ${cx + next},${y1} ${cx - next},${y1}`}
            fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]}
            opacity={stage.count > 0 ? 1 : 0.28}
          />
        );
      })}
    </svg>
  );
}
export { FUNNEL_COLORS };
